import {
    UI5Visibility,
    SYMBOL_KIND_NAMESPACE,
    SYMBOL_KIND_CLASS,
    CLASS_SYMBOL_METADATA_PROPERTY,
    SYMBOL_KIND_TYPEDEF
} from './types';
import type { MacrosAPI, UI5Aggregation, UI5Symbol, ClassSymbol, TypedefSymbol, UI5Property } from './types';
import { v4 } from '@sap/ux-specification-types';

interface APICorrection {
    defaultAggregation?: string;
    aggregations?: UI5Aggregation[];
    removeAggregations?: string[];
    properties?: Array<UI5Property | (Partial<UI5Property> & { extend: true })>;
    metadata?: UI5Symbol;
}

/**
 * Describes a map of enum entries that may depend on another property.
 */
interface MacrosEnumEntriesMap {
    /**
     * Name of the property whose value determines which enum entries are valid.
     */
    dependentProperty: string;

    /**
     * Map of dependent property values to their corresponding enum entries.
     */
    values: { [key: string]: string[] };
}

/**
 * Map defining enum entries for macro controls and their properties.
 *
 * Structure:
 * - First key: control name
 * - Second key: property name
 * - Value: describing dependent values and enum entries
 */
export const MACROS_ENUM_ENTRIES_MAP: {
    [key: string]: { [key: string]: MacrosEnumEntriesMap };
} = {
    'sap.fe.macros.richtexteditor.ButtonGroup': {
        buttons: {
            dependentProperty: 'name',
            values: {
                'font-style': ['bold', 'italic', 'underline', 'strikethrough'],
                font: ['fontfamily', 'fontsize', 'forecolor', 'backcolor'],
                clipboard: ['cut', 'copy', 'paste'],
                structure: ['bullist', 'numlist', 'outdent', 'indent'],
                undo: ['undo', 'redo'],
                insert: ['image', 'emoticons'],
                link: ['link', 'unlink'],
                'text-align': ['alignleft', 'aligncenter', 'alignright', 'alignjustify'],
                table: ['table'],
                styleselect: ['styleselect']
            }
        }
    }
};

/**
 * Returns the enum entries for a given control and property.
 * If a dependent value is provided and exists in the map, only the enum entries
 * associated with that dependent value are returned.
 * Otherwise, all enum entries for the property are returned as a flat array.
 *
 * @param control - The macro control class name
 * @param property - The property name of the control
 * @param dependentValue - Optional value of the dependent property
 * @returns An array of enum entry strings, or `undefined` if the control or property is not found
 */
export function getEnumEntriesFromMap(
    control: string,
    property: string,
    dependentValue?: string
): string[] | undefined {
    const controlEntry = MACROS_ENUM_ENTRIES_MAP[control];
    const propertyEntry = controlEntry?.[property];
    if (!propertyEntry) {
        return;
    }
    const values = propertyEntry.values;
    if (dependentValue && values[dependentValue]) {
        // Return specific entries matching value of dependent property
        return values[dependentValue];
    }
    // Merge and return all entries
    const result: string[] = [];
    for (const name in values) {
        result.push(...values[name]);
    }
    return result;
}

const creationModeProperty: UI5Property = {
    name: 'creationMode',
    bindable: true,
    description: '<p>A set of options that can be configured.</p>',
    group: 'Misc',
    methods: [],
    type: 'sap.fe.macros.table.TableCreationOptions',
    visibility: UI5Visibility.public,
    defaultValue: ''
};
export const macrosCorrection: { [key: string]: APICorrection } = {
    'sap.fe.macros.Page': {
        defaultAggregation: 'items'
    },
    'sap.fe.macros.Table': {
        defaultAggregation: 'columns',
        removeAggregations: ['noData', 'creationMode'],
        properties: [creationModeProperty]
    },
    'sap.fe.macros.table.Table': {
        removeAggregations: ['noData', 'creationMode'],
        properties: [creationModeProperty]
    },
    'sap.fe.macros.table.Column': {
        removeAggregations: ['exportSettings']
    },
    'sap.fe.macros.TreeTable': {
        removeAggregations: ['noData', 'creationMode'],
        properties: [{ ...creationModeProperty, type: 'sap.fe.macros.table.TreeTableCreationOptions' }]
    },
    'sap.fe.macros.table.TreeTable': {
        removeAggregations: ['noData', 'creationMode'],
        properties: [{ ...creationModeProperty, type: 'sap.fe.macros.table.TreeTableCreationOptions' }]
    },
    'sap.fe.macros.Form': {
        defaultAggregation: 'fields',
        aggregations: [
            {
                name: 'fields',
                singularName: 'field',
                type: 'sap.fe.macros.FormElement',
                cardinality: '0..n',
                visibility: UI5Visibility.public,
                description: 'Form fields',
                methods: [],
                linkEnabled: true
            }
        ]
    },
    'sap.fe.macros.ActionGroup': {
        defaultAggregation: 'actions',
        aggregations: [
            {
                name: 'actions',
                singularName: 'action',
                type: 'sap.fe.macros.table.Action',
                cardinality: '0..n',
                visibility: UI5Visibility.public,
                description: 'Table actions',
                methods: [],
                linkEnabled: true
            }
        ],
        properties: [
            {
                defaultValue: '',
                description: 'The text that will be displayed for this action group',
                group: 'Misc',
                methods: [],
                name: 'text',
                types: [
                    {
                        value: 'string'
                    }
                ],
                visibility: UI5Visibility.public
            },
            {
                defaultValue: '',
                description: 'Defines where this action group should be placed relative to the defined anchor',
                group: 'Misc',
                methods: [],
                name: 'placement',
                types: [
                    {
                        value: 'string'
                    }
                ],
                visibility: UI5Visibility.public
            },
            {
                defaultValue: '',
                description:
                    'Reference to the key of another action or action group already displayed in the toolbar to properly place this one',
                group: 'Misc',
                methods: [],
                name: 'anchor',
                types: [
                    {
                        value: 'string'
                    }
                ],
                visibility: UI5Visibility.public
            },
            {
                defaultValue: '',
                description: 'Unique identifier of the ActionGroup',
                group: 'Misc',
                methods: [],
                name: 'key',
                types: [
                    {
                        value: 'string'
                    }
                ],
                visibility: UI5Visibility.public
            }
        ],
        metadata: {
            kind: SYMBOL_KIND_CLASS,
            name: 'sap.fe.macros.ActionGroup',
            basename: 'ActionGroup',
            resource: '',
            module: '',
            static: true,
            visibility: UI5Visibility.public,
            description: 'Definition of a actions group to be used inside the table toolbar',
            displayName: 'sap.fe.macros.ActionGroup',
            title: 'class sap.fe.macros.ActionGroup',
            subTitle: ''
        }
    },
    'sap.fe.macros.richtexteditor.ButtonGroup': {
        properties: [
            {
                name: 'visible',
                extend: true,
                // Correct from string to boolean while fix is not applied from fe team
                type: 'boolean'
            },
            {
                name: 'name',
                extend: true,
                type: 'string',
                disabled: true
            },
            {
                name: 'id',
                extend: true,
                type: 'string',
                visibility: UI5Visibility.hidden
            }
        ]
    },
    'sap.fe.macros.controls.BaseAction': {
        properties: [
            {
                name: 'text',
                extend: true,
                // Correct from any to string while fix is not applied from fe team
                type: 'string'
            },
            {
                name: 'visible',
                extend: true,
                // Correct from any to boolean while fix is not applied from fe team
                type: 'boolean'
            }
        ]
    },
    'sap.fe.macros.RichTextEditor': {
        properties: [
            {
                name: 'required',
                extend: true,
                // Correct from any to boolean while fix is not applied from fe team
                type: 'boolean'
            },
            {
                name: 'value',
                extend: true,
                // Correct from any to string while fix is not applied from fe team
                type: 'string'
            }
        ]
    }
};

const placementEnum = [v4.Placement.After, v4.Placement.Before];

const enumsEntries: {
    [key: string]: {
        [key: string]: {
            options: string[];
            multiSelect?: boolean;
        };
    };
} = {
    'sap.fe.macros.FilterField': {
        placement: {
            options: placementEnum
        }
    },
    'sap.fe.macros.table.Column': {
        placement: {
            options: placementEnum
        }
    },
    'sap.fe.macros.Table': {
        variantManagement: {
            options: ['None', 'Page', 'Control']
        }
    },
    'sap.fe.macros.richtexteditor.ButtonGroup': {
        buttons: {
            options: getEnumEntriesFromMap('sap.fe.macros.richtexteditor.ButtonGroup', 'buttons'),
            multiSelect: true
        },
        name: {
            options: Object.keys(MACROS_ENUM_ENTRIES_MAP['sap.fe.macros.richtexteditor.ButtonGroup'].buttons.values)
        }
    }
};

/**
 * Method returns node with "properties" and "aggregation" for passed symbol.
 * If node does not contains "properties" or "aggregation", then it prepares empty objects.
 *
 * @param {ClassSymbol | TypedefSymbol} symbol Symbol from SAPUI5 api.
 * @returns {APICorrection} Correction object which contains "properties" and "aggregation" properties.
 */
function getNode(symbol: ClassSymbol | TypedefSymbol): APICorrection {
    let node: APICorrection;
    if (symbol.kind === SYMBOL_KIND_CLASS) {
        if (!symbol[CLASS_SYMBOL_METADATA_PROPERTY]) {
            symbol[CLASS_SYMBOL_METADATA_PROPERTY] = {
                aggregations: [],
                properties: [],
                stereotype: ''
            };
        }
        node = symbol[CLASS_SYMBOL_METADATA_PROPERTY];
    } else {
        node = symbol as APICorrection;
    }
    if (!node.aggregations) {
        node.aggregations = [];
    }
    if (!node.properties) {
        node.properties = [];
    }
    return node;
}

/**
 * Method applies correction for passed node.
 *
 * @param {APICorrection} node Original node to update with corrections.
 * @param {APICorrection} correction Correction to apply - "properties" and "aggregation" are applied.
 */
function applyCorrectionForNode(node: APICorrection, correction: APICorrection): void {
    for (const aggregation of correction.aggregations || []) {
        if (!node.aggregations.some((existingAggregation) => existingAggregation.name === aggregation.name)) {
            node.aggregations.push(aggregation);
        }
    }
    for (const aggregation of correction.removeAggregations || []) {
        const aggregationIndex = node.aggregations.findIndex(
            (existingAggregation) => existingAggregation.name === aggregation
        );
        if (aggregationIndex !== -1) {
            node.aggregations.splice(aggregationIndex, 1);
        }
    }
    for (const property of correction.properties || []) {
        const propertyIndex = node.properties.findIndex((existingProperty) => existingProperty.name === property.name);
        // add missing properties or extend existing
        if (propertyIndex !== -1 && 'extend' in property && property.extend) {
            node.properties[propertyIndex] = {
                ...node.properties[propertyIndex],
                ...property
            };
        } else if (propertyIndex === -1) {
            node.properties.push(property);
        }
    }
    if (node.defaultAggregation !== correction.defaultAggregation) {
        node.defaultAggregation = correction.defaultAggregation;
    }
}

/**
 * Method applies correction for enum entries.
 *
 * @param {MacrosAPI} api "sap.fe.macros" api object.
 */
function applyCorrectionForEnums(api: MacrosAPI): void {
    for (const name in enumsEntries) {
        const symbol = api.symbols.find((apiSymbol: UI5Symbol) => apiSymbol.name === name);
        if (![SYMBOL_KIND_TYPEDEF, SYMBOL_KIND_CLASS].includes(symbol?.kind)) {
            continue;
        }
        const properties = enumsEntries[name];
        for (const name in properties) {
            let property: UI5Property | undefined;
            if (symbol?.kind === SYMBOL_KIND_TYPEDEF) {
                property = symbol.properties.find((ui5Property: UI5Property) => ui5Property.name === name);
            } else if (symbol?.kind === SYMBOL_KIND_CLASS) {
                property = symbol[CLASS_SYMBOL_METADATA_PROPERTY].properties.find(
                    (ui5Property: UI5Property) => ui5Property.name === name
                );
            }
            if (property) {
                property.allowedValues = properties[name].options;
                property.multiSelect = properties[name].multiSelect;
            }
        }
    }
}

/**
 * Method applies correction for missing id property in controls.
 *
 * @param {MacrosAPI} api "sap.fe.macros" api object.
 */
function applyCorrectionForIds(api: MacrosAPI): void {
    for (const symbol of api.symbols) {
        if (symbol.kind !== SYMBOL_KIND_CLASS) {
            continue;
        }
        if (!symbol[CLASS_SYMBOL_METADATA_PROPERTY].properties) {
            symbol[CLASS_SYMBOL_METADATA_PROPERTY].properties = [];
        }
        const properties = symbol[CLASS_SYMBOL_METADATA_PROPERTY].properties;
        const id = properties.find((property) => property.name === 'id');
        const description = 'Unique id of control';
        if (!id) {
            properties.push({
                name: 'id',
                bindable: true,
                description,
                group: 'Misc',
                methods: [],
                type: 'string',
                visibility: UI5Visibility.public,
                defaultValue: ''
            });
        } else if (!id.description) {
            id.description = description;
        }
    }
}

/**
 * Method applies missing aggregations and properties for "sap.fe.macros" api object.
 * We notice that api object of "sap.fe.macros" is missing some aggregation and properties information
 * - in result we apply missing data on top of received api object.
 *
 * @param {MacrosAPI} api "sap.fe.macros" api object.
 */
export function applyCorrections(api: MacrosAPI): void {
    // Apply missing classes
    for (const name in macrosCorrection) {
        const symbol = api.symbols.find((apiSymbol: UI5Symbol) => apiSymbol.name === name);
        if (!symbol && macrosCorrection[name].metadata) {
            api.symbols.push({
                ...macrosCorrection[name].metadata
            });
        }
    }
    // Apply missing aggregation and properties
    for (const symbol of api.symbols) {
        const correction = macrosCorrection[symbol.name];
        if (!correction || symbol.kind === SYMBOL_KIND_NAMESPACE) {
            continue;
        }
        const node = getNode(symbol);
        applyCorrectionForNode(node, correction);
    }
    // Enum entries
    applyCorrectionForEnums(api);
    // Missing ids in api.json
    applyCorrectionForIds(api);
}
